import { Injectable, inject } from "@angular/core";
import {
	CreateOrderGQLService,
	UpdateOrderGQLService,
	FetchOrderGQLService,
	CountOrdersGQLService,
	EstimateOrderGQLService,
	FetchOrdersGQLService,
	OrderType,
	CreateOrderRequest,
	OrderField,
	FetchOrdersFilter,
	PaginationInput,
	FetchOrderStatus,
	CurrencyAmountInput,
	UpdateOrderRequest,
	OrderStatus
} from "@gql/offer";
import { Observable, catchError, map, from, of } from "rxjs";
import {
	IEstimateOrderRequestPayload,
	IFetchOrderListRequest,
	IOrder,
	IOrderCount,
	IOrderDetailCreateRequestPayload,
	IOrderEstimate,
	IOrderTotal,
	IOrdersCreateResponse,
	IOrdersList,
	OrderGenerated
} from "../interfaces/limit-orders.remote.interface";
import { ExceptionsService } from "@core-services/exceptions.service";
import { INITIAL_PAGE, PAGE_SIZE } from "../services/limit-orders.service";
import { CancelResponses } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { TotalOrdersGQLService } from "app/api/graphql/offer/generated/order.total.service.graphql-gen";
import { TotalSellOrdersGQLService } from "app/api/graphql/offer/generated/order.total.sell.service.graphql-gen";

@Injectable({ providedIn: "root" })
export class OrdersRemoteService {
	private createOrderGQL = inject(CreateOrderGQLService);
	private countOrdersGQL = inject(CountOrdersGQLService);
	private fetchOrdersGQL = inject(FetchOrdersGQLService);
	private fetchOrderGQL = inject(FetchOrderGQLService);
	private estimateOrderGQL = inject(EstimateOrderGQLService);
	private updateOrderGQL = inject(UpdateOrderGQLService);
	private totalOrderGQL = inject(TotalOrdersGQLService);
	private totalOrderSellGQL = inject(TotalSellOrdersGQLService);
	private exceptionsService = inject(ExceptionsService);

	/**
	 * Create limit order mutation
	 * default for  type is  BUY and amountDefinedFor is FIAT
	 * @param payload: IOrderDetailCreatePayload
	 * @returns - an Observable of IOrdersCreateResponse
	 */
	createOrder$(payload: Partial<IOrderDetailCreateRequestPayload>): Observable<Partial<IOrdersCreateResponse>> {
		const { amount$, price$, fiatId, assetId, assetWalletId, type = OrderType.Buy, visionBonus = false } = payload;

		const input: CreateOrderRequest = {
			amount: { value: amount$ },
			price: { value: price$ },
			fiatId,
			assetId,
			assetWalletId: type === OrderType.Buy ? null : assetWalletId,
			type,
			visionBonus
		};

		return this.createOrderGQL.mutate({ input }).pipe(
			map(({ data }) => {
				//if success response
				if (data.createOrder.__typename === "OrderSuccessResponse") {
					const { orderId, status, type, fiatId, assetId, price, amount, createdAt } = data.createOrder.order;
					return {
						orderId,
						status,
						type,
						fiatId,
						assetId,
						price: price.value,
						amount: amount.value,
						createdAt: createdAt.iso8601,
						orderInProgress: false
					};
				}

				if (data.createOrder.__typename === "OrderInProgressResponse") {
					return { orderInProgress: true, type, amount: amount$, price: price$ };
				}

				//otherwise
				return null;
			}),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * Fetch orders list with pagination
	 * @param request: Partial<IFetchOrderListRequest>
	 * @returns - an Observable of IOrdersList
	 */
	fetchOrdersList$(request: Partial<IFetchOrderListRequest>, orderType?: OrderType): Observable<IOrdersList> {
		const {
			assetId$ = null,
			page$ = INITIAL_PAGE,
			pagesize$ = PAGE_SIZE,
			order = OrderField.CreatedAt,
			status = FetchOrderStatus.Open
		} = request;

		const filter: FetchOrdersFilter = assetId$
			? { assetId: assetId$, status, type: orderType }
			: { status, type: orderType };

		const pagination: PaginationInput = { page: page$, pageSize: pagesize$ };

		return this.fetchOrdersGQL
			.watch({ filter, pagination, order }, { fetchPolicy: "network-only" })
			.valueChanges.pipe(
				map(({ data: { fetchOrders } }) => {
					const orders = fetchOrders.orders.map((order) => this.orderDeserializer(order));
					const pageInfo = fetchOrders.pageInfo;

					return {
						orders,
						pageInfo
					};
				}),
				catchError(this.exceptionsService.extractGqlErrorAndRethrow)
			);
	}

	/**
	 * TODO: uncomment executeAt once schema available from grapQL
	 * Deserialize order object
	 * @param order - Order
	 * @returns - IOrder
	 */
	private orderDeserializer({
		amount,
		assetId,
		createdAt,
		failedAt,
		executedAt,
		failedReason,
		fiatId,
		orderId,
		price,
		status,
		type
	}: OrderGenerated): IOrder {
		return {
			amount: amount.value,
			assetId: assetId,
			createdAt: createdAt?.iso8601,
			failedAt: failedAt?.iso8601,
			executedAt: executedAt?.iso8601,
			failedReason: failedReason,
			fiatId: fiatId,
			orderId: orderId,
			price: price.value,
			status: status,
			type: type
		};
	}

	/**
	 * Fetch order detail
	 * @param id - string - the order ID
	 * @returns - an Observable of IOrder of single order
	 */
	fetchOrderDetail$(orderId: string): Observable<IOrder> {
		return this.fetchOrderGQL.watch({ orderId }, { fetchPolicy: "network-only" }).valueChanges.pipe(
			map(({ data: { fetchOrder } }) => {
				return this.orderDeserializer(fetchOrder);
			}),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * Fetch orders count
	 * @param id - string - the order ID
	 * @returns - an Observable of IOrderCount
	 */
	getOrderCount$(assetId: string = ""): Observable<IOrderCount> {
		return from(
			this.countOrdersGQL
				.watch({ filter: { assetId } }, { fetchPolicy: "network-only" })
				.refetch()
				.then(({ data: { countOrders } }) => countOrders)
		).pipe(catchError(this.exceptionsService.extractGqlErrorAndRethrow));
	}

	/**
	 * Fetch order estimation for value and itb
	 * @param id - string - the order ID
	 * @returns - an Observable of IOrderEstimate
	 */
	estimateOrder$(request: IEstimateOrderRequestPayload): Observable<IOrderEstimate> {
		const { amount$, targetPrice$, assetId, fiatId, type, visionBonus = null } = request;
		const amount: CurrencyAmountInput = { value: amount$ };
		const price: CurrencyAmountInput = { value: targetPrice$ };

		return this.estimateOrderGQL
			.watch({ amount, price, assetId, fiatId, type, visionBonus }, { fetchPolicy: "network-only" })
			.valueChanges.pipe(
				map(
					({
						data: {
							estimateOrder: {
								value,
								isFeeTransparent,
								feePercentage,
								userJourney,
								vsnFeeTokenCollection
							}
						}
					}) => {
						return {
							value: value?.value,
							isFeeTransparent,
							feePercentage: feePercentage?.value,
							userJourney,
							vsnFeeTokenCollection
						};
					}
				),
				catchError(() =>
					of({
						value: "0",
						isFeeTransparent: true,
						feePercentage: null,
						vsnFeeTokenCollection: null
					})
				)
			);
	}

	/**
	 * update order
	 * @param orderId - string - the order ID
	 * @returns - an Observable of IOrder
	 */
	updateOrder$(orderId: string, status: OrderStatus): Observable<Partial<IOrder>> {
		const input: UpdateOrderRequest = { orderId, status };

		return this.updateOrderGQL.mutate({ input }).pipe(
			map(({ data: { updateOrder } }) => {
				if (updateOrder.__typename === CancelResponses.Success) {
					return this.orderDeserializer(updateOrder.order);
				}
				if (updateOrder.__typename === CancelResponses.Pending) {
					return { orderInProgress: true };
				}
			}),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * total order buy
	 * @param fiatId - string -
	 * @returns - an Observable of IOrder
	 */
	totalOrder$(fiatId?: string): Observable<IOrderTotal[]> {
		return this.totalOrderGQL.watch({ filter: { fiatId } }, { fetchPolicy: "no-cache" }).valueChanges.pipe(
			map(({ data }) => {
				return data.totalOrders.fiats.map((total) => {
					const {
						amount: { value },
						fiatId
					} = total;
					return {
						fiatId,
						amount: value
					};
				});
			}),
			catchError(() => of([]))
		);
	}

	/**
	 * total order sell
	 * @param filter - {assetWalletId: string} -
	 * @returns - an Observable of IOrder
	 */
	totalSellOrder$(assetWalletId: string): Observable<number> {
		return this.totalOrderSellGQL
			.watch({ filter: { assetWalletId } }, { fetchPolicy: "no-cache" })
			.valueChanges.pipe(
				map(({ data: { totalSellOrders } }) => {
					return +totalSellOrders.amount?.value;
				}),
				catchError(() => of(0)) // supress error and return 0
			);
	}
}
